// Loads a single channel's config from Dexie: the channel record and its breadcrumbs.
//
// Every return value has a `status` string ('idle', 'loading', 'ready', 'unavailable')
// so callers just check status instead of comparing against
// DEXIE_IS_LOADING or CHANNEL_NOT_FOUND.
import { useLiveQuery } from 'dexie-react-hooks';

import db from '../lib/db';

import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';

const CHANNEL_CONFIG_IDLE = { status: 'idle', channel: null, breadcrumbs: [] };
const CHANNEL_CONFIG_LOADING = { status: 'loading', channel: null, breadcrumbs: [] };
const CHANNEL_CONFIG_UNAVAILABLE = { status: 'unavailable', channel: null, breadcrumbs: [] };

const buildBreadcrumbs = async (workspaceId, channel) => {
  const breadcrumbs = [];
  let node = channel;
  const seen = new Set();

  while (node) {
    breadcrumbs.push(node);
    if (!node.parent_id || seen.has(node.parent_id)) break;
    seen.add(node.id);
    const parent = await db.channels.get([workspaceId, node.parent_id]);
    if (!parent) break;
    node = parent;
  }

  return breadcrumbs.slice().reverse();
};

const useChannelConfig = (channelId) => {
  const { workspaceId } = useRouteWorkspace();
  return useLiveQuery(
    async () => {
      if (!channelId) return CHANNEL_CONFIG_IDLE;
      if (!workspaceId) return CHANNEL_CONFIG_LOADING;

      const channel = await db.channels.get([workspaceId, String(channelId)]);
      if (!channel) {
        // If Dexie has no channels for this workspace yet, it's still hydrating.
        const channelsCount = await db.channels.where('workspace_id').equals(workspaceId).count();
        if (channelsCount === 0) return CHANNEL_CONFIG_LOADING;
        return CHANNEL_CONFIG_UNAVAILABLE;
      }

      const breadcrumbs = await buildBreadcrumbs(workspaceId, channel);

      return { status: 'ready', channel, breadcrumbs };
    },
    [channelId, workspaceId],
    CHANNEL_CONFIG_LOADING,
  );
};

export default useChannelConfig;
